import type { BrewKey, Product, RoastKey } from '@c214/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost, apiPut } from '../lib/api';
import { useAuth } from './useAuth';
import { PRODUCTS_QUERY_KEY } from './useProducts';

export type AdminProduct = Product & { isActive: boolean };
export type ProductStatus = 'active' | 'archived';

export interface ProductFormInput {
  name: string;
  packSubtitle: string;
  region: string;
  roast: RoastKey;
  brew: BrewKey[];
  price: number;
  score: string;
  altitude: string;
  notes: string;
  description: string;
  acid: number;
  bitter: number;
  body: number;
  roastedAt: string;
  shipsAt: string;
  inSubscription: boolean;
}

export function useAdminProducts(status: ProductStatus) {
  const { data: user } = useAuth();
  return useQuery({
    queryKey: ['admin', 'products', 'list', status] as const,
    queryFn: async () =>
      (await apiGet<{ products: AdminProduct[] }>(`/api/admin/products?status=${status}`)).products,
    enabled: user?.role === 'admin',
  });
}

export function useAdminProduct(id: number | undefined) {
  const { data: user } = useAuth();
  return useQuery({
    queryKey: ['admin', 'products', 'detail', id] as const,
    queryFn: () => apiGet<AdminProduct>(`/api/admin/products/${id}`),
    enabled: user?.role === 'admin' && id !== undefined,
  });
}

// Магазин має одразу бачити зміни — скидаємо і адмінські, і публічні списки товарів.
function useInvalidateProducts() {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: ['admin', 'products'] });
    queryClient.invalidateQueries({ queryKey: PRODUCTS_QUERY_KEY });
    queryClient.invalidateQueries({ queryKey: ['product'] });
  };
}

export function useSaveProduct() {
  const invalidate = useInvalidateProducts();
  return useMutation({
    mutationFn: ({ id, input }: { id?: number; input: ProductFormInput }) =>
      id === undefined
        ? apiPost<AdminProduct>('/api/admin/products', input)
        : apiPut<AdminProduct>(`/api/admin/products/${id}`, input),
    onSuccess: invalidate,
  });
}

export function useSetProductActive() {
  const invalidate = useInvalidateProducts();
  return useMutation({
    mutationFn: ({ id, active }: { id: number; active: boolean }) =>
      apiPost<AdminProduct>(`/api/admin/products/${id}/${active ? 'restore' : 'archive'}`),
    onSuccess: invalidate,
  });
}
